import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Settings } from "lucide-react";

import { cn } from "lib/utils";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import { getLeadSource } from "routes/leads/leadSourceRegistry";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "components/ui/sidebar";
import { Skeleton } from "components/ui/skeleton";

function AccountSwitcherTrigger({ className, ...props }) {
  return (
    <SidebarMenuButton
      size="lg"
      render={<button type="button" />}
      className={cn(
        "tw:data-open:bg-sidebar-accent tw:data-open:text-sidebar-accent-foreground",
        className
      )}
      {...props}
    />
  );
}

// Sidebar header control for a lead-source account detail page: names the
// current connected account and switches between the other accounts of the same
// service. The account list is the same one the nav dropdown and manage page
// read, so it is keyed per service and shared across those consumers.
const LeadAccountSwitcher = () => {
  const { service, accountId } = useParams();
  const { activeTab: tab } = useAppTabs({
    basePath: `/leads/${service}/${accountId}`,
  });
  const navigate = useNavigate();
  const source = getLeadSource(service);
  const canManage = useAuthorized(source ? [source.manageAcl] : []);

  const { data, isLoading } = useQuery({
    queryKey: ["leads-switcher", service],
    // A stale session can 403 this in the background; the switcher degrades to
    // showing just the current account, so suppress the global forbidden modal.
    queryFn: () =>
      source.listConnections(
        { per_page: 100, sort_by: "name", sort_dir: "asc" },
        { suppressForbiddenModal: true }
      ),
    enabled: !!source,
  });

  if (!source) {
    return null;
  }

  const Icon = source.icon;
  const accounts = data?.data ?? [];
  const current = accounts.find(
    account => String(account.id) === String(accountId)
  );

  const goToAccount = id =>
    navigate(
      tab ? `/leads/${service}/${id}/${tab}` : `/leads/${service}/${id}`
    );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={<AccountSwitcherTrigger />}>
            {Icon && <Icon />}
            {isLoading && !current ? (
              <Skeleton className="tw:h-4 tw:flex-1 tw:group-data-[collapsible=icon]:hidden" />
            ) : (
              <span className="tw:flex-1 tw:truncate tw:text-left tw:font-medium tw:group-data-[collapsible=icon]:hidden">
                {current?.name ?? source.label}
              </span>
            )}
            <ChevronsUpDown className="tw:opacity-60 tw:group-data-[collapsible=icon]:hidden" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="tw:min-w-56">
            {accounts.length === 0 && !isLoading && (
              <div className="tw:px-1.5 tw:py-1 tw:text-sm tw:text-muted-foreground">
                No connected accounts
              </div>
            )}
            {accounts.map(account => (
              <DropdownMenuItem
                key={account.id}
                onClick={() => goToAccount(account.id)}
              >
                <span className="tw:truncate">{account.name}</span>
                {String(account.id) === String(accountId) && (
                  <Check className="tw:ml-auto" />
                )}
              </DropdownMenuItem>
            ))}
            {canManage && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  render={<Link to={`/settings/integrations/${service}`} />}
                >
                  <Settings />
                  Manage accounts
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default LeadAccountSwitcher;
